import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/apiClient";
import { toast } from "sonner";
import { X, User, Stethoscope, Pill, AlertTriangle, FileText, Save, Loader2 } from "lucide-react";

/**
 * PatientChartModal — a doctor/patient/admin can view an aggregated chart
 * and (if the viewer is a doctor with a booking) author a new encounter
 * note. Access rules are enforced server-side; this component simply
 * shows whatever the server returns.
 */
export default function PatientChartModal({ patientId, patientName, canAuthor = false, bookingId, onClose, onNoteSaved }) {
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [meds, setMeds] = useState("");
  const [allergies, setAllergies] = useState("");

  useEffect(() => {
    setLoading(true);
    api
      .get(`/ehr/chart/${patientId}`)
      .then((r) => setChart(r.data))
      .catch((e) => setErr(extractError(e)))
      .finally(() => setLoading(false));
  }, [patientId]);

  const save = async () => {
    if (!note.trim()) {
      toast.error("Please write a short note before saving.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/ehr/encounters", {
        patient_id: patientId,
        booking_id: bookingId || null,
        note: note.trim(),
        medications: meds.split(",").map((s) => s.trim()).filter(Boolean),
        allergies: allergies.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Encounter note saved to the chart.");
      setNote("");
      setMeds("");
      setAllergies("");
      // reload chart
      const r = await api.get(`/ehr/chart/${patientId}`);
      setChart(r.data);
      onNoteSaved?.();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="chart-modal"
    >
      <div className="w-full max-w-3xl bg-white rounded-3xl shadow-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-[color:var(--mano-border)] sticky top-0 bg-white rounded-t-3xl z-10">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[color:var(--mano-primary)] text-white flex items-center justify-center">
              <User size={18} />
            </div>
            <div>
              <div className="font-display text-lg">{patientName || chart?.patient?.name || "Patient"}</div>
              <div className="uppercase-label">EHR · Chart summary</div>
            </div>
          </div>
          <button
            data-testid="chart-close"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[color:var(--mano-border)]/40"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {loading && (
            <div className="text-center py-8 text-[color:var(--mano-ink-soft)] inline-flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Loading chart…
            </div>
          )}
          {err && !loading && (
            <div className="rounded-2xl bg-red-50 border border-red-200 p-4 text-sm text-red-700" data-testid="chart-error">
              {err}
            </div>
          )}
          {chart && (
            <>
              <section className="grid sm:grid-cols-2 gap-3">
                <ChartField label="Email" value={chart.patient.email} />
                <ChartField label="Phone" value={chart.patient.phone} />
                <ChartField label="DOB" value={chart.patient.dob} />
                <ChartField label="Gender" value={chart.patient.gender} />
              </section>

              <section className="grid sm:grid-cols-2 gap-3">
                <PillList
                  testid="chart-medications"
                  label="Medications"
                  icon={Pill}
                  items={chart.medications}
                />
                <PillList
                  testid="chart-allergies"
                  label="Allergies"
                  icon={AlertTriangle}
                  items={chart.allergies}
                  tone="warning"
                />
              </section>

              <section data-testid="chart-encounters">
                <div className="uppercase-label inline-flex items-center gap-1">
                  <FileText size={14} /> Recent encounter notes
                </div>
                <div className="mt-3 space-y-2">
                  {chart.recent_encounters.length === 0 && (
                    <div className="text-sm text-[color:var(--mano-ink-soft)] italic">No encounter notes on file yet.</div>
                  )}
                  {chart.recent_encounters.map((e) => (
                    <div key={e.id} className="rounded-2xl border border-[color:var(--mano-border)] p-4 bg-[color:var(--mano-bg)]">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-sm font-semibold inline-flex items-center gap-1">
                          <Stethoscope size={12} /> {e.doctor_name || "Specialist"}
                        </div>
                        <div className="text-xs text-[color:var(--mano-ink-soft)]">
                          {new Date(e.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-sm mt-2 whitespace-pre-wrap">{e.note}</div>
                      {(e.medications?.length || e.allergies?.length) ? (
                        <div className="text-xs text-[color:var(--mano-ink-soft)] mt-2 space-x-3">
                          {e.medications?.length ? <span>Meds: {e.medications.join(", ")}</span> : null}
                          {e.allergies?.length ? <span>Allergies: {e.allergies.join(", ")}</span> : null}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <div className="uppercase-label">Recent voice reports</div>
                <div className="mt-2 rounded-2xl border border-[color:var(--mano-border)] divide-y divide-[color:var(--mano-border)]">
                  {chart.recent_reports.length === 0 && (
                    <div className="p-4 text-sm text-[color:var(--mano-ink-soft)]">No reports yet.</div>
                  )}
                  {chart.recent_reports.map((r) => (
                    <div key={r.id} className="p-3 flex items-center justify-between text-sm">
                      <div className="capitalize">
                        {(r.category || "").replace("_", " ")} · {new Date(r.created_at).toLocaleDateString()}
                      </div>
                      <div className="uppercase-label">{r.severity_band}</div>
                    </div>
                  ))}
                </div>
              </section>

              {canAuthor && (
                <section
                  className="rounded-2xl border border-[color:var(--mano-primary)]/40 bg-[color:var(--mano-primary)]/5 p-4"
                  data-testid="chart-note-authoring"
                >
                  <div className="uppercase-label inline-flex items-center gap-1">
                    <FileText size={14} /> Add encounter note
                  </div>
                  <textarea
                    data-testid="chart-note-input"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={4}
                    placeholder="Chief complaint, observations, plan…"
                    className="mt-3 w-full rounded-xl border border-[color:var(--mano-border)] bg-white p-3 text-sm"
                  />
                  <div className="mt-3 grid sm:grid-cols-2 gap-3">
                    <input
                      data-testid="chart-note-meds"
                      value={meds}
                      onChange={(e) => setMeds(e.target.value)}
                      placeholder="Medications (comma-separated)"
                      className="h-11 rounded-xl border border-[color:var(--mano-border)] bg-white px-3 text-sm"
                    />
                    <input
                      data-testid="chart-note-allergies"
                      value={allergies}
                      onChange={(e) => setAllergies(e.target.value)}
                      placeholder="Allergies (comma-separated)"
                      className="h-11 rounded-xl border border-[color:var(--mano-border)] bg-white px-3 text-sm"
                    />
                  </div>
                  <div className="mt-3 flex items-center justify-end">
                    <button
                      data-testid="chart-note-save"
                      onClick={save}
                      disabled={saving}
                      className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white font-semibold text-sm disabled:opacity-60"
                    >
                      {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                      Save to chart
                    </button>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ChartField({ label, value }) {
  return (
    <div className="rounded-xl border border-[color:var(--mano-border)] bg-white p-3">
      <div className="uppercase-label">{label}</div>
      <div className="text-sm mt-1">{value || <span className="text-[color:var(--mano-ink-soft)] italic">—</span>}</div>
    </div>
  );
}

function PillList({ label, items, icon: Icon, tone = "primary", testid }) {
  const toneCls =
    tone === "warning"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : "bg-[color:var(--mano-primary)]/10 text-[color:var(--mano-primary)] border-[color:var(--mano-primary)]/20";
  return (
    <div className="rounded-xl border border-[color:var(--mano-border)] bg-white p-3" data-testid={testid}>
      <div className="uppercase-label inline-flex items-center gap-1">
        <Icon size={12} /> {label}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {(!items || items.length === 0) && (
          <span className="text-xs text-[color:var(--mano-ink-soft)] italic">None on file</span>
        )}
        {(items || []).map((i) => (
          <span key={i} className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${toneCls}`}>
            {i}
          </span>
        ))}
      </div>
    </div>
  );
}
